// components/Dashboard/tabs/platforms/XChannels.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import XModal from '../../../Modals/XModal/XModal';
import XBulkModal from '../../../Modals/XBulkModal/XBulkModal';
import './XChannels.css';

const XChannels = forwardRef(({
  showMessage,
  API_URL,
  onNavigateToLibrary,
  onNavigateToAutomation
}, ref) => {
  const [xAccounts, setXAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({});
  const [automationRules, setAutomationRules] = useState([]);
  const [tags, setTags] = useState([]);
  const [showTweetModal, setShowTweetModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkMode, setBulkMode] = useState('tweet');

  useImperativeHandle(ref, () => ({
    handleRefresh: refreshAccounts,
    handleAddChannel: connectXAccount
  }));

  const fetchAutomationRules = async () => {
    try {
      const response = await fetch(`${API_URL}/automation`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setAutomationRules(data.rules || []);
    } catch (error) {
      console.error('Otomasyon kuralları yüklenemedi:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/tags`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setTags(data.tags || []);
    } catch (error) {
      console.error('Etiketler yüklenemedi:', error);
    }
  };

  const findMatchingAutomationRules = (accountId) => {
    return automationRules.filter(rule => {
      if (!rule.enable_x_sharing || rule.is_active !== 1) return false;
      if (!rule.x_account_ids) return false;
      try {
        const ids = typeof rule.x_account_ids === 'string'
          ? JSON.parse(rule.x_account_ids) : rule.x_account_ids;
        if (Array.isArray(ids)) return ids.includes(accountId);
      } catch (e) { }
      return false;
    });
  };

  const getAccountAutomationSchedule = (accountId) => {
    const accountRules = findMatchingAutomationRules(accountId);
    if (accountRules.length === 0) return null;
    let allTimes = [];
    accountRules.forEach(rule => {
      if (rule.daily_times) {
        try {
          const times = JSON.parse(rule.daily_times);
          if (Array.isArray(times) && times.length > 0) allTimes = [...allTimes, ...times];
        } catch (e) { }
      }
    });
    allTimes.sort((a, b) => a.hour !== b.hour ? a.hour - b.hour : a.minute - b.minute);
    const uniqueTimes = allTimes.filter((time, index, self) =>
      index === self.findIndex(t => t.hour === time.hour && t.minute === time.minute)
    );
    const now = new Date();
    let nextTimeIndex = uniqueTimes.findIndex(t =>
      t.hour > now.getHours() || (t.hour === now.getHours() && t.minute > now.getMinutes())
    );
    if (nextTimeIndex === -1 && uniqueTimes.length > 0) nextTimeIndex = 0;
    return { allTimes: uniqueTimes, nextTimeIndex, rulesCount: accountRules.length };
  };

  const getAccountActiveAutomationTags = (accountId) => {
    const accountRules = findMatchingAutomationRules(accountId);
    let allTagNames = [];
    accountRules.forEach(rule => {
      if (rule.tags) allTagNames = [...allTagNames, ...rule.tags.split(',').map(t => t.trim())];
    });
    return [...new Set(allTagNames)]
      .map(name => tags.find(tag => tag.name.toLowerCase() === name.toLowerCase()))
      .filter(Boolean);
  };

  const handleAutomationClick = (accountId, e) => {
    e.stopPropagation();
    const accountRules = findMatchingAutomationRules(accountId);
    if (accountRules.length > 0 && onNavigateToAutomation) onNavigateToAutomation(accountRules[0].id);
  };

  const handleTagClick = (tagId, e) => {
    e.stopPropagation();
    if (onNavigateToLibrary) onNavigateToLibrary(tagId);
  };

  const fetchXAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/platforms/twitter/accounts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Hesaplar yüklenemedi');
      const data = await response.json();

      // GetLate formatını normalize et
      const accounts = (data.accounts || []).map(acc => ({
        id: acc._id || acc.id,
        name: acc.displayName || acc.username || acc.name,
        username: acc.username,
        profile_image_url: acc.profilePicture || acc.profile_image_url,
        is_active: acc.isActive !== false,
        profileName: acc.profileName
      }));

      setXAccounts(accounts);
      if (accounts.length > 0) {
        fetchAccountStats(accounts.map(acc => acc.id));
      }
    } catch (error) {
      console.error('X hesapları yüklenirken hata:', error);
      showMessage('X hesapları yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountStats = async (accountIds) => {
    try {
      const results = await Promise.allSettled(
        accountIds.map(id =>
          fetch(`${API_URL}/platforms/twitter/accounts/${id}/stats`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }).then(res => res.json())
        )
      );
      const newStats = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') newStats[accountIds[index]] = result.value;
      });
      setStats(newStats);
    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
    }
  };

  const connectXAccount = async () => {
    try {
      const response = await fetch(`${API_URL}/platforms/twitter/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Bağlantı başlatılamadı');
      const data = await response.json();

      const width = 600, height = 700;
      const left = window.innerWidth / 2 - width / 2;
      const top = window.innerHeight / 2 - height / 2;
      const authWindow = window.open(data.authUrl, 'X OAuth', `width=${width},height=${height},left=${left},top=${top}`);

      const checkInterval = setInterval(() => {
        if (authWindow && authWindow.closed) {
          clearInterval(checkInterval);
          setTimeout(() => { fetchXAccounts(); showMessage('X hesabı başarıyla bağlandı!', 'success'); }, 1000);
        }
      }, 1000);
    } catch (error) {
      showMessage('X hesabı bağlanamadı', 'error');
    }
  };

  const disconnectAccount = async (accountId, username) => {
    if (!window.confirm(`@${username} hesabının bağlantısını kaldırmak istediğinizden emin misiniz?`)) return;
    try {
      const response = await fetch(`${API_URL}/platforms/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Bağlantı kaldırılamadı');
      showMessage('Hesap bağlantısı kaldırıldı', 'success');
      fetchXAccounts();
    } catch (error) {
      showMessage('Bağlantı kaldırılamadı', 'error');
    }
  };

  const openTweetModal = (account) => { setSelectedAccount(account); setShowTweetModal(true); };
  const closeTweetModal = () => { setShowTweetModal(false); setSelectedAccount(null); };
  const openBulkModal = (mode) => { setBulkMode(mode); setShowBulkModal(true); };
  const closeBulkModal = () => { setShowBulkModal(false); };

  const refreshAccounts = async () => {
    setRefreshing(true);
    await fetchXAccounts();
    await fetchAutomationRules();
    await fetchTags();
    setRefreshing(false);
    showMessage('Hesaplar yenilendi', 'success');
  };

  const formatCount = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toLocaleString('tr-TR');
  };

  const formatLastUpdate = (dateString) => {
    if (!dateString) return 'Güncellenmemiş';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) {
      if (diffMins < 1) return 'Şimdi';
      if (diffMins < 60) return `${diffMins} dk önce`;
      return `${diffHours} saat önce`;
    }
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  useEffect(() => {
    fetchXAccounts();
    fetchAutomationRules();
    fetchTags();
  }, []);

  if (loading && xAccounts.length === 0) {
    return (
      <div className="xc-loading-state">
        <div className="xc-loading-spinner"></div>
        <p>Hesaplar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="xc-x-channels-container">
      {/* Toplu İşlem Butonları */}
      {xAccounts.length > 0 && (
        <div className="xc-bulk-actions">
          <button className="xc-bulk-btn xc-bulk-tweet" onClick={() => openBulkModal('tweet')}>
            📢 Toplu Tweet Gönder
          </button>
          <button className="xc-bulk-btn xc-bulk-retweet" onClick={() => openBulkModal('retweet')}>
            🔁 Toplu RT Yap
          </button>
        </div>
      )}

      {xAccounts.length === 0 ? (
        <div className="xc-empty-state">
          <div className="xc-empty-icon">
            <svg viewBox="0 0 24 24" width="64" height="64" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </div>
          <h3>Henüz X hesabı bağlanmadı</h3>
          <p>İçeriklerinizi X'te paylaşmak için hesabınızı bağlayın</p>
          <button className="xc-empty-add-btn" onClick={connectXAccount}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            X Hesabı Bağla
          </button>
        </div>
      ) : (
        <div className="xc-channels-grid">
          {xAccounts.map(account => {
            const automationData = getAccountAutomationSchedule(account.id);
            const activeTags = getAccountActiveAutomationTags(account.id);
            const accountStats = stats[account.id] || {};

            return (
              <div key={account.id} className="xc-channel-card">
                {/* HEADER */}
                <div className="xc-card-header">
                  <div className="xc-channel-avatar">
                    {account.profile_image_url ? (
                      <img src={account.profile_image_url} alt={account.name} />
                    ) : (
                      <span>𝕏</span>
                    )}
                  </div>
                  <div className="xc-channel-info">
                    <div className="xc-channel-name-row">
                      <span className="xc-channel-name" title={account.name}>
                        {account.name}
                      </span>
                      <a
                        href={`https://x.com/${account.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="xc-external-link"
                        onClick={(e) => e.stopPropagation()}
                        title="X'te aç"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                      </a>
                      <span className={`xc-status-dot ${account.is_active ? 'xc-active' : 'xc-inactive'}`}
                        title={account.is_active ? 'Aktif' : 'Pasif'} />
                    </div>
                    <a
                      href={`https://x.com/${account.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="xc-channel-handle"
                    >
                      @{account.username}
                    </a>
                  </div>
                </div>

                {/* STATS */}
                <div className="xc-stats-row">
                  <div className="xc-stat-box">
                    <span className="xc-stat-icon">👥</span>
                    <div className="xc-stat-content">
                      <span className="xc-stat-value">{formatCount(accountStats.followers_count)}</span>
                      <span className="xc-stat-label">Takipçi</span>
                    </div>
                  </div>
                  <div className="xc-stat-box">
                    <span className="xc-stat-icon">📝</span>
                    <div className="xc-stat-content">
                      <span className="xc-stat-value">{formatCount(accountStats.tweet_count)}</span>
                      <span className="xc-stat-label">Tweet</span>
                    </div>
                  </div>
                </div>

                {/* OTOMASYON */}
                {automationData && automationData.allTimes.length > 0 ? (
                  <div
                    className="xc-automation-section"
                    onClick={(e) => handleAutomationClick(account.id, e)}
                    title="Otomasyonu düzenlemek için tıklayın"
                  >
                    <div className="xc-automation-header-row">
                      <span className="xc-automation-icon">🤖</span>
                      <span className="xc-automation-label">
                        Her gün {automationData.allTimes.length} paylaşım
                        {automationData.rulesCount > 1 && ` · ${automationData.rulesCount} kural`}
                      </span>
                    </div>
                    <div className="xc-automation-times">
                      {automationData.allTimes.map((time, index) => (
                        <span
                          key={`${time.hour}-${time.minute}`}
                          className={`xc-time-chip ${index === automationData.nextTimeIndex ? 'xc-time-next' : ''}`}
                        >
                          {String(time.hour).padStart(2, '0')}:{String(time.minute).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="xc-automation-section xc-no-automation">
                    <span className="xc-automation-icon">🔕</span>
                    <span className="xc-no-automation-text">Otomasyon ayarlanmamış</span>
                  </div>
                )}

                {/* ETİKETLER */}
                {activeTags.length > 0 && (
                  <div className="xc-tags-section">
                    {activeTags.map(tag => (
                      <span
                        key={tag.id}
                        className="xc-tag-chip"
                        onClick={(e) => handleTagClick(tag.id, e)}
                        style={{ '--tag-color': tag.color }}
                        title={`${tag.name} etiketli içerikleri görüntüle`}
                      >
                        🏷️ {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* TWEET BUTONU */}
                <div className="xc-tweet-action">
                  <button className="xc-tweet-btn" onClick={() => openTweetModal(account)}>
                    🚀 Tweet Gönder
                  </button>
                </div>

                {/* FOOTER */}
                <div className="xc-card-footer">
                  <div className="xc-footer-info">
                    <span className="xc-footer-profile">GetLate</span>
                    {account.last_post_at && (
                      <span className="xc-footer-date">{formatLastUpdate(account.last_post_at)}</span>
                    )}
                  </div>
                  <div className="xc-footer-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); disconnectAccount(account.id, account.username); }}
                      className="xc-action-btn xc-delete-btn"
                      title="Bağlantıyı Kes"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <XModal isOpen={showTweetModal} onClose={closeTweetModal} selectedAccount={selectedAccount} showMessage={showMessage} API_URL={API_URL} />
      <XBulkModal isOpen={showBulkModal} onClose={closeBulkModal} accounts={xAccounts} mode={bulkMode} showMessage={showMessage} API_URL={API_URL} />
    </div>
  );
});

XChannels.displayName = 'XChannels';
export default XChannels;